// docs/38 BS7 — the edge of a free-form field. Drawn over the ground and every layer under the
// units (light, props, shadows, low effects, fog), so nothing those layers paint shows on a void
// square: void squares take the scene's backdrop, the field's rim darkens softly where the ground
// ends, and a short bank face hangs under each south-facing edge so the field reads as a raised
// piece of country rather than a cut-out. A rectangle board draws nothing here.
import Phaser from 'phaser';
import { fieldEdges, mergeRuns, type FieldShape } from './field';

/** The scene backdrop (`Game.ts` backgroundColor). */
export const BACKDROP = 0x0b1020;
/** The bank under a south-facing edge: dark earth, then a fade into the backdrop. */
const BANK = 0x2a2219;
/** The rim inside the field: strips of darkness, outermost first (fraction of a cell, alpha). */
const RIM: ReadonlyArray<readonly [number, number]> = [[0.08, 0.34], [0.16, 0.18], [0.26, 0.08]];

export class VoidLayer {
  private g?: Phaser.GameObjects.Graphics;
  /** Void squares covered on the last draw (tests). */
  covered = 0;

  constructor(private readonly scene: Phaser.Scene, private readonly layer: Phaser.GameObjects.Layer, private readonly cell: number) {}

  draw(board: FieldShape): void {
    this.g?.destroy();
    this.g = undefined;
    const e = fieldEdges(board);
    this.covered = e.cover.length;
    if (e.cover.length === 0) return;
    const c = this.cell;
    const g = this.scene.add.graphics();
    this.layer.add(g);
    this.g = g;
    // a few hundred merged rects, not one per square (the Graphics redraws them all every frame);
    // the very same pixels: covers are opaque, and the banks and rims are strips along their runs
    g.fillStyle(BACKDROP, 1);
    // a hair of overlap so no seam of the ground shows between neighbouring covers
    for (const r of mergeRuns(e.cover)) g.fillRect(r.x * c - 0.5, r.y * c - 0.5, r.w * c + 1, r.h * c + 1);
    const b0 = Math.round(c * 0.22), b1 = Math.round(c * 0.12), b2 = Math.round(c * 0.1);
    for (const r of mergeRuns(e.banks, false)) {
      const x = r.x * c, y = r.y * c, w = r.w * c;
      g.fillStyle(BANK, 1); g.fillRect(x, y, w, b0);
      g.fillStyle(BANK, 0.55); g.fillRect(x, y + b0, w, b1);
      g.fillStyle(BANK, 0.25); g.fillRect(x, y + Math.round(c * 0.34), w, b2);
    }
    // north and south rims run along a row, west and east ones down a column (a run of strips is one
    // strip; the rims are all black, so where two sides meet in a corner they darken it as before)
    for (const side of ['n', 's', 'w', 'e'] as const) {
      const along = side === 'n' || side === 's';
      const runs = mergeRuns(e.rims.filter((r) => r.side === side).map((r) => (along ? { x: r.x, y: r.y } : { x: r.y, y: r.x })), false);
      for (const run of runs) {
        const x = (along ? run.x : run.y) * c, y = (along ? run.y : run.x) * c, len = run.w * c;
        let from = 0;
        for (const [depth, alpha] of RIM) {
          const to = Math.round(c * depth);
          g.fillStyle(0x000000, alpha);
          if (side === 'n') g.fillRect(x, y + from, len, to - from);
          else if (side === 's') g.fillRect(x, y + c - to, len, to - from);
          else if (side === 'w') g.fillRect(x + from, y, to - from, len);
          else g.fillRect(x + c - to, y, to - from, len);
          from = to;
        }
      }
    }
  }

  destroy(): void { this.g?.destroy(); this.g = undefined; }
}
